// Режим Макса: ключи изменяющих ручек (`tmp_limited`) и сами ручки.
// Ключ вводится руками и живёт только в sessionStorage этой вкладки:
// закрыл вкладку, простоял IDLE_LIMIT_MS или получил 401 — ключ стёрт.

import store from "@/store/store";

const STORAGE_KEY = "maxAccess";
const ACTIVITY_KEY = "maxAccessActivity";
const JOURNAL_KEY = "maxJournal";
const IDLE_LIMIT_MS = 30 * 60 * 1000;

export const SITES = ["rbxtree", "rbxpier"];

export const SITE_NAMES = {
  rbxtree: "RobuxTree",
  rbxpier: "RobuxPier"
};

// Хосты те же, что у verify_info: auth.rbxtree.gg из спеки редиректит на rbxtree.io, auth.robuxpier.com не отвечает
const BALANCE_URLS = {
  rbxtree: "https://auth.backend-tree.com/api/v1/user/temp-balance-limited",
  rbxpier: "https://users.backend-pier.com/api/v1/user/temp-balance-limited"
};

export const BALANCE_LIMITS = {
  perRequest: 3000,
  perUserDaily: 3000,
  totalDaily: 10000
};

function readKeys() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeKeys(keys) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  store.commit(
    "setPrivilegedSites",
    SITES.reduce((acc, site) => ({ ...acc, [site]: Boolean(keys[site]) }), {})
  );
}

export function getKey(site) {
  return readKeys()[site] || null;
}

export function setKey(site, key) {
  writeKeys({ ...readKeys(), [site]: key });
  touch();
}

export function clearKey(site) {
  const keys = readKeys();
  delete keys[site];
  writeKeys(keys);
}

export function clearAll() {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(ACTIVITY_KEY);
  writeKeys({});
}

export function touch() {
  sessionStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

// Вызывается при старте и по таймеру: восстанавливает флаги в сторе и гасит режим по бездействию.
export function syncState() {
  const lastActivity = Number(sessionStorage.getItem(ACTIVITY_KEY)) || 0;
  if (Object.keys(readKeys()).length && Date.now() - lastActivity > IDLE_LIMIT_MS) {
    clearAll();
    return;
  }
  writeKeys(readKeys());
}

// Через сколько миллисекунд обнулятся дневные лимиты (00:00 UTC)
export function msUntilLimitReset() {
  const now = new Date();
  const reset = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return reset - now.getTime();
}

function formatDuration(ms) {
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

const BALANCE_ERRORS = {
  400: "Отклонено: пользователь не найден, сумма больше 3000 ₽ за раз или баланс ушёл бы в минус.",
  401: "Неверный ключ доступа. Режим изменений выключен — введите ключ заново.",
  403: "Списание не прошло: баланс изменился между проверкой и списанием. Обновите карточку и повторите."
};

function limitMessage() {
  return (
    "Дневной лимит исчерпан: 3000 ₽ на пользователя или 10 000 ₽ на всех за сутки. " +
    `Сброс в 03:00 МСК (через ${formatDuration(msUntilLimitReset())}). ` +
    "Срочное начисление — через Рому или бэк."
  );
}

// amount: > 0 — начислить, < 0 — списать. reason уходит в тело для лога на бэке.
export async function changeBalance(site, { userId, amount, reason }) {
  const key = getKey(site);
  if (!key) {
    return { ok: false, code: 401, message: BALANCE_ERRORS[401] };
  }
  touch();

  let response;
  try {
    response = await fetch(BALANCE_URLS[site], {
      method: "POST",
      // Ключ в теле, не заголовком: CORS на auth-сервисах разрешает только content-type
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, amount, reason, key })
    });
  } catch (error) {
    return {
      ok: false,
      code: 0,
      message:
        "Не удалось получить ответ сервера. Баланс мог успеть измениться — обновите карточку пользователя перед повтором."
    };
  }

  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }

  if (response.ok && data.status) {
    return { ok: true, code: response.status };
  }
  if (response.status === 401) {
    clearKey(site);
  }
  if (response.status === 429) {
    return { ok: false, code: 429, message: limitMessage() };
  }

  const internal = data.error && data.error.internal;
  return {
    ok: false,
    code: response.status,
    message: BALANCE_ERRORS[response.status] || `Ошибка сервиса (${response.status})${internal ? `: ${internal}` : ""}`
  };
}

// Рефанд заказов: возврат на баланс пользователя.
const REFUND_URLS = {
  rbxtree: "https://pending.backend-tree.com/admin/refund_orders",
  rbxpier: "https://transfer.backend-pier.com/admin/refund_orders"
};

export const REFUND_BATCH = 100;

const REFUND_MESSAGES = {
  "invalid order_id": "Неверный ID",
  "order not found": "Заказ не найден"
};

// Перевод message из results. systemError — статус уже сменён, а деньги могли не дойти: только ручная проверка, без повтора
export function refundMessage(message) {
  if (!message) return { label: "Не удалось" };
  if (message.includes("system_error")) {
    return { label: "Сбой: заказ в system_error — проверить вручную, дошли ли деньги", systemError: true };
  }
  if (message.startsWith("wrong status:")) {
    return { label: `Нельзя: статус ${message.slice("wrong status:".length).trim()}, деньги не начислялись` };
  }
  return { label: REFUND_MESSAGES[message] || message };
}

// Сумма рефанда: дерево отдаёт rub_amount, пристань — amount: { rub, usd, eur }
export function refundAmount(item) {
  if (!item) return {};
  if (item.amount && typeof item.amount === "object") return item.amount;
  if (item.rub_amount != null) return { rub: Number(item.rub_amount) || 0 };
  return {};
}

// Одна пачка до REFUND_BATCH ID. Ответ: { status, refunded, failed, refunded_rub, results: [{ order_id, status, message?, rub_amount? }] }
// Ключ — тот же, что у /temp-balance-limited (ключ Макса). Кладём в тело под обоими именами: admin_key
// (исходный контракт ручки) и key (как у баланса). Заголовок не шлём, чтобы не упереться в CORS на pending.
export async function refundOrders(site, { orderIds, reason }) {
  const key = getKey(site);
  if (!key) {
    return { ok: false, code: 401, message: BALANCE_ERRORS[401] };
  }
  touch();
  let response;
  try {
    response = await fetch(REFUND_URLS[site], {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ admin_key: key, key, order_ids: orderIds, reason })
    });
  } catch (error) {
    return { ok: false, message: "Нет ответа сервера. Часть заказов могла рефанднуться — перепроверьте статусы." };
  }

  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }

  if (response.status === 401 || response.status === 403) {
    clearKey(site);
    return { ok: false, code: 401, message: BALANCE_ERRORS[401] };
  }
  if (!response.ok || !Array.isArray(data.results)) {
    const details = data.message || (data.error && JSON.stringify(data.error)) || `код ${response.status}`;
    return { ok: false, message: `Сервер отклонил запрос (${details})` };
  }
  return { ok: true, data };
}

// Журнал операций текущей сессии — чтобы Макс видел, что уже сделал
export function readJournal() {
  try {
    return JSON.parse(sessionStorage.getItem(JOURNAL_KEY)) || [];
  } catch (e) {
    return [];
  }
}

export function addJournalEntry(entry) {
  const journal = [{ ...entry, time: new Date().toISOString() }, ...readJournal()].slice(0, 50);
  sessionStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));
  return journal;
}
